import { useState, useRef, useMemo, memo, useCallback } from 'react';
import { PackTimeseriesPoint } from '../../types/apogee';
import { SkeletonLoader } from '../SkeletonLoader';
import { EmptyState } from '../ui/EmptyState';
import { TrendingUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';

interface PackTimeseriesChartProps {
  data: PackTimeseriesPoint[];
  height?: number;
  isLoading?: boolean;
  title?: string;
  showRank?: boolean;
  emptyMessage?: string;
}

const PADDING = { top: 24, right: 56, bottom: 48, left: 64 };
const VIEWBOX_WIDTH = 900;

const safeDownloads = (d: PackTimeseriesPoint): number => {
  const v = d.download_count;
  return typeof v === 'number' && isFinite(v) ? v : 0;
};

const safeRank = (d: PackTimeseriesPoint): number | null => {
  const v = d.popularity_rank;
  return typeof v === 'number' && isFinite(v) ? v : null;
};

const safeDateStr = (d: PackTimeseriesPoint): string => {
  return (d.t || '').replace(' ', 'T');
};

const ChartSkeleton = ({ height }: { height: number }) => (
  <div className="glass-card p-4 md:p-5 flex flex-col" style={{ height: height + 80 }} aria-busy="true">
    <SkeletonLoader className="h-4 w-48 mb-4 shrink-0" />
    <SkeletonLoader className="flex-1 w-full rounded-lg" />
  </div>
);

// Theme-aware SVG colours (CSS variables flip with the active theme).
const COLOR_DOWNLOADS = 'var(--accent-300)';
const COLOR_RANK = 'var(--tone-amber-text)';
const COLOR_GRID = 'var(--border-subtle)';
const COLOR_LABEL = 'var(--text-muted)';
const COLOR_POINT_STROKE = 'var(--bg-elevated)';

export const PackTimeseriesChart = memo(({
  data,
  height = 300,
  isLoading,
  title,
  showRank = false,
  emptyMessage = 'No timeseries data available.',
}: PackTimeseriesChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const viewboxHeight = height;
  const chartWidth = VIEWBOX_WIDTH - PADDING.left - PADDING.right;
  const chartHeight = viewboxHeight - PADDING.top - PADDING.bottom;

  const { downloadPath, rankPath, xLabels, yLabels, yRankLabels, points } = useMemo(() => {
    if (!data || data.length === 0) {
      return { downloadPath: '', rankPath: '', xLabels: [], yLabels: [], yRankLabels: [], points: [] };
    }

    const downloads = data.map(safeDownloads);
    const minDl = Math.min(...downloads);
    const maxDl = Math.max(...downloads);
    const dlPad = (maxDl - minDl) * 0.05 || 1;
    const dlMin = Math.max(0, minDl - dlPad);
    const dlMax = maxDl + dlPad;

    const ranks = data.map(safeRank).filter((r): r is number => r !== null);
    const minRank = ranks.length > 0 ? Math.min(...ranks) : 1;
    const maxRank = ranks.length > 0 ? Math.max(...ranks) : 100;
    const rankPad = Math.max(1, (maxRank - minRank) * 0.05);

    const pts: { x: number; yDl: number; yRank: number | null }[] = data.map((d, i) => {
      const x = PADDING.left + (i / Math.max(data.length - 1, 1)) * chartWidth;
      const yDl = PADDING.top + chartHeight - ((safeDownloads(d) - dlMin) / (dlMax - dlMin || 1)) * chartHeight;
      const rank = safeRank(d);
      // Rank: lower is better — so rank 1 should be at the top
      const yRank = rank !== null
        ? PADDING.top + ((rank - (minRank - rankPad)) / ((maxRank + rankPad) - (minRank - rankPad) || 1)) * chartHeight
        : null;
      return { x, yDl, yRank };
    });

    const dlPath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.yDl.toFixed(1)}`).join('');
    const rPath = showRank
      ? pts
          .map((p, i) => (p.yRank !== null ? `${i === 0 || pts[i - 1].yRank === null ? 'M' : 'L'}${p.x.toFixed(1)},${p.yRank.toFixed(1)}` : ''))
          .filter(Boolean)
          .join('')
      : '';

    // X labels (6 evenly spaced)
    const labelCount = Math.min(6, data.length);
    const xLbls = Array.from({ length: labelCount }).map((_, i) => {
      const idx =
        labelCount <= 1 ? 0 : Math.round((i / (labelCount - 1)) * (data.length - 1));
      const d = data[idx];
      return {
        x: pts[idx].x,
        label: format(parseISO(safeDateStr(d)), 'MMM d'),
      };
    });

    // Y labels (downloads — 5 ticks)
    const yLbls = Array.from({ length: 5 }).map((_, i) => {
      const val = dlMin + (i / 4) * (dlMax - dlMin);
      const y = PADDING.top + chartHeight - (i / 4) * chartHeight;
      return { y, label: val >= 1000 ? `${(val / 1000).toFixed(0)}K` : val.toFixed(0) };
    });

    // Y labels (rank — 4 ticks)
    const yRLbls = showRank && ranks.length > 0
      ? Array.from({ length: 4 }).map((_, i) => {
          const val = (minRank - rankPad) + (i / 3) * ((maxRank + rankPad) - (minRank - rankPad));
          const y = PADDING.top + (i / 3) * chartHeight;
          return { y, label: `#${Math.round(val)}` };
        })
      : [];

    return {
      downloadPath: dlPath,
      rankPath: rPath,
      xLabels: xLbls,
      yLabels: yLbls,
      yRankLabels: yRLbls,
      points: pts,
    };
  }, [data, chartWidth, chartHeight, showRank]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!containerRef.current || !data || data.length === 0) return;
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = ((e.clientX - rect.left) / rect.width) * VIEWBOX_WIDTH;
      const relX = mouseX - PADDING.left;
      const idx = Math.round((relX / chartWidth) * (data.length - 1));
      setHoveredIndex(Math.max(0, Math.min(data.length - 1, idx)));
    },
    [data, chartWidth],
  );

  if (isLoading) return <ChartSkeleton height={height} />;

  if (!data || data.length === 0) {
    return (
      <div style={{ minHeight: Math.min(height, 240) }} className="flex">
        <EmptyState
          compact
          icon={TrendingUp}
          title={title ?? 'Timeseries'}
          description={emptyMessage}
          className="flex-1"
        />
      </div>
    );
  }

  const hovered = hoveredIndex !== null ? data[hoveredIndex] : null;
  const hoveredPt = hoveredIndex !== null ? points[hoveredIndex] : null;

  return (
    <div className="glass-card p-4 md:p-5" ref={containerRef}>
      {title && (
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-accent-300 shrink-0" />
            {title}
          </h3>
          {showRank && (
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-0.5 rounded" style={{ background: COLOR_DOWNLOADS }} />
                <span className="text-glass-400">Downloads</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-0.5 rounded" style={{ background: COLOR_RANK }} />
                <span className="text-glass-400">Rank</span>
              </span>
            </div>
          )}
        </div>
      )}

      <svg
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${viewboxHeight}`}
        className="w-full"
        style={{ height }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {/* Grid lines */}
        {yLabels.map((yl, i) => (
          <line
            key={`grid-${i}`}
            x1={PADDING.left}
            y1={yl.y}
            x2={VIEWBOX_WIDTH - PADDING.right}
            y2={yl.y}
            stroke={COLOR_GRID}
            strokeDasharray="4,4"
          />
        ))}

        {/* Download area fill */}
        {downloadPath && (
          <path
            d={`${downloadPath}L${points[points.length - 1].x.toFixed(1)},${(PADDING.top + chartHeight).toFixed(1)}L${points[0].x.toFixed(1)},${(PADDING.top + chartHeight).toFixed(1)}Z`}
            fill="url(#downloadGradient)"
            opacity={0.3}
          />
        )}

        {/* Download line */}
        <path d={downloadPath} fill="none" stroke={COLOR_DOWNLOADS} strokeWidth={2.5} strokeLinejoin="round" />

        {/* Rank line */}
        {showRank && rankPath && (
          <path d={rankPath} fill="none" stroke={COLOR_RANK} strokeWidth={2} strokeLinejoin="round" strokeDasharray="6,3" />
        )}

        {/* X labels */}
        {xLabels.map((xl, i) => (
          <text key={`x-${i}`} x={xl.x} y={viewboxHeight - 8} fill={COLOR_LABEL} fontSize="11" textAnchor="middle">
            {xl.label}
          </text>
        ))}

        {/* Y labels (downloads) */}
        {yLabels.map((yl, i) => (
          <text key={`y-${i}`} x={PADDING.left - 8} y={yl.y + 4} fill={COLOR_LABEL} fontSize="11" textAnchor="end">
            {yl.label}
          </text>
        ))}

        {/* Y labels (rank) */}
        {yRankLabels.map((yl, i) => (
          <text key={`yr-${i}`} x={VIEWBOX_WIDTH - PADDING.right + 8} y={yl.y + 4} fill={COLOR_RANK} fillOpacity={0.7} fontSize="10" textAnchor="start">
            {yl.label}
          </text>
        ))}

        {/* Hover indicator */}
        {hoveredPt && (
          <>
            <line
              x1={hoveredPt.x}
              y1={PADDING.top}
              x2={hoveredPt.x}
              y2={PADDING.top + chartHeight}
              stroke={COLOR_LABEL}
              strokeOpacity={0.5}
              strokeDasharray="3,3"
            />
            <circle cx={hoveredPt.x} cy={hoveredPt.yDl} r={4} fill={COLOR_DOWNLOADS} stroke={COLOR_POINT_STROKE} strokeWidth={1.5} />
            {hoveredPt.yRank !== null && showRank && (
              <circle cx={hoveredPt.x} cy={hoveredPt.yRank} r={3.5} fill={COLOR_RANK} stroke={COLOR_POINT_STROKE} strokeWidth={1.5} />
            )}
          </>
        )}

        {/* Gradient definition */}
        <defs>
          <linearGradient id="downloadGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLOR_DOWNLOADS} stopOpacity="0.4" />
            <stop offset="100%" stopColor={COLOR_DOWNLOADS} stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      {/* Tooltip */}
      {hovered && (
        <div className="flex items-center justify-center gap-4 mt-2 text-xs tabular-nums">
          <span className="text-glass-400">
            {format(parseISO(safeDateStr(hovered)), 'MMM d, yyyy HH:mm')}
          </span>
          <span className="text-accent-300 font-medium">
            {safeDownloads(hovered).toLocaleString()} downloads
          </span>
          {safeRank(hovered) !== null && (
            <span className="text-amber-400 font-medium">
              Rank #{safeRank(hovered)}
            </span>
          )}
        </div>
      )}
    </div>
  );
});

PackTimeseriesChart.displayName = 'PackTimeseriesChart';
